import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router-dom';
import { Mail, Lock, User, Users, MessageCircle, Info, Eye, EyeOff } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Button } from '../components/ui/Button';
import { Alert } from '../components/ui/Alert';
import { UnifiedHeader } from '../components/layout/UnifiedHeader';
import { useAuth } from '../hooks/useAuth';
import { Logo } from '../components/ui/Logo';
import { RegisterFormData } from '../types/auth';
import { passwordSchema } from '../utils/validation';
import { SEO } from '../components/seo/SEO';
import { PAGE_META } from '../config/seo';
import { safeInternalPath } from '../utils/safeInternalPath';

const registerSchema = z.object({
  firstName: z
    .string()
    .min(1, 'First name is required')
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name must be less than 50 characters')
    .regex(/^[a-zA-Z'.,\- ]+$/, 'First name can only contain letters, spaces, hyphens, apostrophes, commas, and periods'),
  lastName: z
    .string()
    .min(1, 'Last name is required')
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name must be less than 50 characters')
    .regex(/^[a-zA-Z'.,\- ]+$/, 'Last name can only contain letters, spaces, hyphens, apostrophes, commas, and periods'),
  middleName: z
    .string()
    .max(50, 'Middle name must be less than 50 characters')
    .regex(/^[a-zA-Z'.,\- ]*$/, 'Middle name can only contain letters, spaces, hyphens, apostrophes, commas, and periods')
    .optional()
    .or(z.literal('')),
  email: z
    .string()
    .min(1, 'Email is required')
    .email('Please enter a valid email address')
    .max(100, 'Email must be less than 100 characters'),
  password: z
    .string()
    .min(1, 'Password is required')
    .max(128, 'Password must be less than 128 characters')
    .pipe(passwordSchema),
  confirmPassword: z
    .string()
    .min(1, 'Password confirmation is required'),
  // Optional registration info fields (help admins verify faster)
  referredBy: z
    .string()
    .max(200, 'Must be less than 200 characters')
    .optional()
    .or(z.literal('')),
  howDidYouHear: z
    .enum(['Family member', 'Social media', 'Word of mouth', 'Church/community', 'Other'])
    .or(z.literal(''))
    .optional(),
  familyConnection: z
    .string()
    .max(500, 'Must be less than 500 characters')
    .optional()
    .or(z.literal(''))
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"]
});

const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { register: registerUser, isLoading, error, clearError, isAuthenticated } = useAuth();

  // Get redirect URL from location state (set by ProtectedRoute) or search params, default to home.
  // Keep search + hash: deep links like /gallery/albums/{id}?media={id} must
  // survive the login round-trip, not just the pathname. safeInternalPath
  // pins the target to an in-app path (no open redirect via ?redirect=).
  const fromLocation = location.state?.from;
  const from = fromLocation?.pathname
    ? `${fromLocation.pathname}${fromLocation.search || ''}${fromLocation.hash || ''}`
    : searchParams.get('redirect');
  const redirectTo = safeInternalPath(from);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(redirectTo, { replace: true });
    }
  }, [isAuthenticated, navigate, redirectTo]);

  const {
    register: registerForm,
    handleSubmit,
    formState: { errors, isValid },
    clearErrors,
    watch,
    setValue
  } = useForm<RegisterFormData>({
    resolver: zodResolver(registerSchema),
    mode: 'onChange'
  });

  const password = watch('password', '');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showVerificationFields, setShowVerificationFields] = useState(false);

  const handleVerificationToggle = (checked: boolean) => {
    setShowVerificationFields(checked);
    if (!checked) {
      setValue('referredBy', '', { shouldValidate: false });
      setValue('howDidYouHear', '', { shouldValidate: false });
      setValue('familyConnection', '', { shouldValidate: false });
    }
  };

  const handleFormSubmit = async (data: RegisterFormData) => {
    try {
      clearError();
      clearErrors();
      await registerUser(data);
      // Navigate to success page with registration data
      navigate('/registration-success', { 
        replace: true, 
        state: {
          firstName: data.firstName,
          email: data.email,
          registeredAt: new Date().toISOString()
        }
      });
    } catch (error) {
      if (import.meta.env.MODE === 'development') {
        console.error('Registration failed:', error);
      }
    }
  };

  // Password strength indicator
  const getPasswordStrength = (password: string) => {
    const checks = [
      { regex: /.{8,}/, label: 'At least 8 characters' },
      { regex: /[A-Z]/, label: 'One uppercase letter' },
      { regex: /[a-z]/, label: 'One lowercase letter' },
      { regex: /[0-9]/, label: 'One number' }
    ];

    return checks.map(check => ({
      ...check,
      met: check.regex.test(password)
    }));
  };

  const passwordChecks = getPasswordStrength(password);
  const passwordStrengthMet = passwordChecks.filter(check => check.met).length;

  // Skip link for keyboard navigation
  const skipToMain = () => {
    const mainContent = document.getElementById('main-content');
    if (mainContent) {
      mainContent.focus();
    }
  };

  return (
    <div className="min-h-screen bg-surface-0">
      <SEO title={PAGE_META['/register'].title} description={PAGE_META['/register'].description} path="/register" />
      {/* Skip to main content link for screen readers */}
      <button
        onClick={skipToMain}
        className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-brand text-white px-4 py-2 rounded-md z-50"
      >
        Skip to main content
      </button>

      {/* Header */}
      <UnifiedHeader />

      {/* Main Content */}
      <main id="main-content" className="flex items-center justify-center px-4 py-12 pt-28 min-h-screen" tabIndex={-1}>
        <div className="max-w-md w-full">
          {/* Logo Mark */}
          <div className="flex justify-center mb-8">
            <Logo size="md" />
          </div>

          {/* Card */}
          <div className="bg-white rounded-lg border border-border shadow-sm p-8">
            {/* Heading */}
            <div className="text-center mb-6">
              <h1 className="text-2xl font-semibold text-text-primary mb-2">
                Join the Family
              </h1>
              <p className="text-sm text-text-secondary">
                Create your account to join <span className="font-display">The Redd Family</span> Association
              </p>
            </div>

            {/* General Error Message */}
            {error && (
              <Alert variant="error" title="Registration Error" className="mb-6">
                {error.message}
              </Alert>
            )}

            {/* Register Form */}
            <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-5">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label htmlFor="register-firstname" className="block text-sm font-medium text-text-primary mb-2">
                    First Name
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-4 w-4 text-text-tertiary" aria-hidden="true" />
                    </div>
                    <input
                      {...registerForm('firstName')}
                      id="register-firstname"
                      type="text"
                      autoComplete="given-name"
                      required
                      disabled={isLoading}
                      className="block w-full pl-10 pr-3 py-2.5 border border-border rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 disabled:bg-surface-1 disabled:text-text-tertiary transition-colors"
                      placeholder="First name"
                      aria-invalid={errors.firstName ? 'true' : 'false'}
                      aria-describedby={errors.firstName ? 'register-firstname-error' : undefined}
                    />
                  </div>
                  {errors.firstName && (
                    <p id="register-firstname-error" className="mt-2 text-sm text-error flex items-center gap-1" role="alert">
                      <span className="text-error" aria-hidden="true">⚠</span>
                      {errors.firstName.message}
                    </p>
                  )}
                </div>

                <div>
                  <label htmlFor="register-lastname" className="block text-sm font-medium text-text-primary mb-2">
                    Last Name
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-4 w-4 text-text-tertiary" aria-hidden="true" />
                    </div>
                    <input
                      {...registerForm('lastName')}
                      id="register-lastname"
                      type="text"
                      autoComplete="family-name"
                      required
                      disabled={isLoading}
                      className="block w-full pl-10 pr-3 py-2.5 border border-border rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 disabled:bg-surface-1 disabled:text-text-tertiary transition-colors"
                      placeholder="Last name"
                      aria-invalid={errors.lastName ? 'true' : 'false'}
                      aria-describedby={errors.lastName ? 'register-lastname-error' : undefined}
                    />
                  </div>
                  {errors.lastName && (
                    <p id="register-lastname-error" className="mt-2 text-sm text-error flex items-center gap-1" role="alert">
                      <span className="text-error" aria-hidden="true">⚠</span>
                      {errors.lastName.message}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="register-middlename" className="block text-sm font-medium text-text-primary mb-2">
                  Middle Name <span className="text-text-tertiary text-xs">(optional)</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <User className="h-4 w-4 text-text-tertiary" aria-hidden="true" />
                  </div>
                  <input
                    {...registerForm('middleName')}
                    id="register-middlename"
                    type="text"
                    autoComplete="additional-name"
                    disabled={isLoading}
                    className="block w-full pl-10 pr-3 py-2.5 border border-border rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 disabled:bg-surface-1 disabled:text-text-tertiary transition-colors"
                    placeholder="Middle name (optional)"
                    aria-invalid={errors.middleName ? 'true' : 'false'}
                    aria-describedby={errors.middleName ? 'register-middlename-error' : undefined}
                  />
                </div>
                {errors.middleName && (
                  <p id="register-middlename-error" className="mt-2 text-sm text-error flex items-center gap-1" role="alert">
                    <span className="text-error" aria-hidden="true">⚠</span>
                    {errors.middleName.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="register-email" className="block text-sm font-medium text-text-primary mb-2">
                  Email Address
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Mail className="h-4 w-4 text-text-tertiary" aria-hidden="true" />
                  </div>
                  <input
                    {...registerForm('email')}
                    id="register-email"
                    type="email"
                    autoComplete="email"
                    required
                    disabled={isLoading}
                    className="block w-full pl-10 pr-3 py-2.5 border border-border rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 disabled:bg-surface-1 disabled:text-text-tertiary transition-colors"
                    placeholder="Enter your email"
                    aria-invalid={errors.email ? 'true' : 'false'}
                    aria-describedby={errors.email ? 'register-email-error' : undefined}
                  />
                </div>
                {errors.email && (
                  <p id="register-email-error" className="mt-2 text-sm text-error flex items-center gap-1" role="alert">
                    <span className="text-error" aria-hidden="true">⚠</span>
                    {errors.email.message}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="register-password" className="block text-sm font-medium text-text-primary mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-4 w-4 text-text-tertiary" aria-hidden="true" />
                  </div>
                  <input
                    {...registerForm('password')}
                    id="register-password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    disabled={isLoading}
                    className="block w-full pl-10 pr-10 py-2.5 border border-border rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 disabled:bg-surface-1 disabled:text-text-tertiary transition-colors"
                    placeholder="Create a secure password"
                    aria-invalid={errors.password ? 'true' : 'false'}
                    aria-describedby={errors.password ? 'register-password-error' : 'register-password-help'}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="text-text-tertiary hover:text-text-secondary focus:outline-none focus:text-text-secondary transition-colors"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                {errors.password && (
                  <p id="register-password-error" className="mt-2 text-sm text-error flex items-center gap-1" role="alert">
                    <span className="text-error" aria-hidden="true">⚠</span>
                    {errors.password.message}
                  </p>
                )}
                
                {/* Password Strength Indicator */}
                {password && (
                  <div className="mt-3 bg-surface-1 border border-border rounded-lg p-3 text-xs" aria-live="polite">
                    <p className="font-medium text-text-primary mb-2">Password Requirements:</p>
                    <div className="grid grid-cols-1 gap-1">
                      {passwordChecks.map((check, index) => (
                        <div key={index} className="flex items-center space-x-2">
                          <span 
                            className={`w-3 h-3 text-xs ${check.met ? 'text-green-600' : 'text-text-tertiary'}`}
                            aria-hidden="true" 
                          >
                            {check.met ? '✓' : '○'}
                          </span>
                          <span className={check.met ? 'text-green-700' : 'text-text-secondary'}>
                            {check.label}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2">
                      <div className="flex space-x-1" role="progressbar" aria-valuenow={passwordStrengthMet} aria-valuemin={0} aria-valuemax={4}>
                        {Array.from({ length: 4 }, (_, i) => (
                          <div
                            key={i}
                            className={`h-1 rounded-full flex-1 ${
                              i < passwordStrengthMet
                                ? passwordStrengthMet <= 1 
                                  ? 'bg-red-500'
                                  : passwordStrengthMet <= 2
                                  ? 'bg-yellow-500'
                                  : 'bg-green-500'
                                : 'bg-surface-2'
                            }`}
                            aria-hidden="true"
                          />
                        ))}
                      </div>
                      <p className={`text-xs mt-1 font-medium ${
                        passwordStrengthMet <= 1 ? 'text-red-700' :
                        passwordStrengthMet <= 2 ? 'text-yellow-700' :
                        'text-green-700'
                      }`}>
                        {passwordStrengthMet <= 1 ? 'Weak' :
                         passwordStrengthMet <= 2 ? 'Fair' :
                         passwordStrengthMet === 3 ? 'Good' : 'Strong'} Password
                      </p>
                    </div>
                  </div>
                )}
                
                <p id="register-password-help" className="mt-2 text-sm text-text-secondary">
                  Must include uppercase, lowercase, and number (min. 8 chars)
                </p>
              </div>

              <div>
                <label htmlFor="register-confirm-password" className="block text-sm font-medium text-text-primary mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Lock className="h-4 w-4 text-text-tertiary" aria-hidden="true" />
                  </div>
                  <input
                    {...registerForm('confirmPassword')}
                    id="register-confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    required
                    disabled={isLoading}
                    className="block w-full pl-10 pr-10 py-2.5 border border-border rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 disabled:bg-surface-1 disabled:text-text-tertiary transition-colors"
                    placeholder="Confirm your password"
                    aria-invalid={errors.confirmPassword ? 'true' : 'false'}
                    aria-describedby={errors.confirmPassword ? 'register-confirm-password-error' : undefined}
                  />
                  <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="text-text-tertiary hover:text-text-secondary focus:outline-none focus:text-text-secondary transition-colors"
                      aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                    >
                      {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>
                {errors.confirmPassword && (
                  <p id="register-confirm-password-error" className="mt-2 text-sm text-error flex items-center gap-1" role="alert">
                    <span className="text-error" aria-hidden="true">⚠</span>
                    {errors.confirmPassword.message}
                  </p>
                )}
              </div>

              {/* Optional: Help Us Verify You (Collapsible) */}
              <div className="border-t border-border pt-5 mt-2">
                <label
                  htmlFor="show-verification-fields"
                  className="flex items-start space-x-3 cursor-pointer group"
                >
                  <div className="relative flex items-center pt-0.5">
                    <input
                      id="show-verification-fields"
                      type="checkbox"
                      checked={showVerificationFields}
                      onChange={(e) => handleVerificationToggle(e.target.checked)}
                      disabled={isLoading}
                      aria-controls="verification-fields-panel"
                      className="h-4 w-4 text-brand focus:ring-brand focus:ring-2 border-border rounded transition-colors"
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <Info className="h-4 w-4 text-brand flex-shrink-0" aria-hidden="true" />
                      <p className="text-sm font-medium text-text-primary group-hover:text-brand transition-colors">
                        I'd like to help verify my identity
                      </p>
                    </div>
                    <p className="text-xs text-text-secondary mt-1 ml-6">
                      Providing this information helps speed up your account approval.
                    </p>
                  </div>
                </label>

                {showVerificationFields && (
                  <div id="verification-fields-panel" role="region" aria-label="Identity verification fields" className="mt-4 p-4 bg-surface-1 rounded-lg animate-slide-down space-y-4">
                    <div>
                      <label htmlFor="register-referred-by" className="block text-sm font-medium text-text-primary mb-2">
                        Family Member Who Can Vouch for You <span className="text-text-tertiary text-xs">(optional)</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <Users className="h-4 w-4 text-text-tertiary" aria-hidden="true" />
                        </div>
                        <input
                          {...registerForm('referredBy')}
                          id="register-referred-by"
                          type="text"
                          disabled={isLoading}
                          className="block w-full pl-10 pr-3 py-2.5 border border-border rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 disabled:bg-surface-1 disabled:text-text-tertiary transition-colors"
                          placeholder="e.g., John Redd"
                          aria-invalid={errors.referredBy ? 'true' : 'false'}
                          aria-describedby={errors.referredBy ? 'register-referred-by-error' : undefined}
                        />
                      </div>
                      {errors.referredBy && (
                        <p id="register-referred-by-error" className="mt-2 text-sm text-error flex items-center gap-1" role="alert">
                          <span className="text-error" aria-hidden="true">⚠</span>
                          {errors.referredBy.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="register-how-did-you-hear" className="block text-sm font-medium text-text-primary mb-2">
                        How Did You Hear About Us? <span className="text-text-tertiary text-xs">(optional)</span>
                      </label>
                      <div className="relative">
                        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                          <MessageCircle className="h-4 w-4 text-text-tertiary" aria-hidden="true" />
                        </div>
                        <select
                          {...registerForm('howDidYouHear')}
                          id="register-how-did-you-hear"
                          disabled={isLoading}
                          className="block w-full pl-10 pr-3 py-2.5 border border-border rounded-lg text-text-primary focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 disabled:bg-surface-1 disabled:text-text-tertiary transition-colors appearance-none bg-white"
                          aria-invalid={errors.howDidYouHear ? 'true' : 'false'}
                          aria-describedby={errors.howDidYouHear ? 'register-how-did-you-hear-error' : undefined}
                        >
                          <option value="">Select an option</option>
                          <option value="Family member">Family member</option>
                          <option value="Social media">Social media</option>
                          <option value="Word of mouth">Word of mouth</option>
                          <option value="Church/community">Church/community</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>
                      {errors.howDidYouHear && (
                        <p id="register-how-did-you-hear-error" className="mt-2 text-sm text-error flex items-center gap-1" role="alert">
                          <span className="text-error" aria-hidden="true">⚠</span>
                          {errors.howDidYouHear.message}
                        </p>
                      )}
                    </div>

                    <div>
                      <label htmlFor="register-family-connection" className="block text-sm font-medium text-text-primary mb-2">
                        Describe Your Connection to the Family <span className="text-text-tertiary text-xs">(optional)</span>
                      </label>
                      <div className="relative">
                        <div className="absolute top-3 left-0 pl-3 flex items-start pointer-events-none">
                          <User className="h-4 w-4 text-text-tertiary" aria-hidden="true" />
                        </div>
                        <textarea
                          {...registerForm('familyConnection')}
                          id="register-family-connection"
                          rows={3}
                          disabled={isLoading}
                          className="block w-full pl-10 pr-3 py-2.5 border border-border rounded-lg text-text-primary placeholder-text-tertiary focus:outline-none focus:border-brand focus:ring-2 focus:ring-brand/20 disabled:bg-surface-1 disabled:text-text-tertiary transition-colors resize-none"
                          placeholder="e.g., daughter of James and Mary Redd"
                          aria-invalid={errors.familyConnection ? 'true' : 'false'}
                          aria-describedby={errors.familyConnection ? 'register-family-connection-error' : undefined}
                        />
                      </div>
                      {errors.familyConnection && (
                        <p id="register-family-connection-error" className="mt-2 text-sm text-error flex items-center gap-1" role="alert">
                          <span className="text-error" aria-hidden="true">⚠</span>
                          {errors.familyConnection.message}
                        </p>
                      )}
                    </div>
                  </div>
                )}
              </div>

              <Button
                type="submit"
                size="lg"
                fullWidth
                loading={isLoading}
                disabled={!isValid || isLoading}
                className="bg-brand text-white hover:bg-brand-hover transition-colors"
              >
                Join the Family
              </Button>
            </form>

            {/* Login Toggle */}
            <div className="mt-6 pt-6 border-t border-border text-center">
              <p className="text-text-secondary text-sm">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="font-medium text-brand hover:text-brand-hover focus:outline-none focus:ring-2 focus:ring-brand/20 rounded transition-colors"
                >
                  Sign in here
                </Link>
              </p>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export { RegisterPage };
